import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { ArrowLeft, Loader2, AlertTriangle, Plus, BarChart3, Monitor } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ServiceOrderForm } from '../components/project/ServiceOrderForm';
import { ServiceOrderManagementCard } from '../components/project/ServiceOrderManagementCard';
import { UnassignedTasksPool } from '../components/project/UnassignedTasksPool';
import { TaskForm } from '../components/project/TaskForm';
import { TicketForm } from '../components/project/TicketForm';
import { Ticket, CreateTicketRequest } from '../types/ticket';
import { createTicket, getTickets, updateTicket as updateTicketApi, deleteTicket as deleteTicketApi } from '../services/ticketsApi';
import { ticketAreaToTaskArea } from '../components/project/DraggableTicketCard';
import socketService from '../services/socket';
import { 
  ServiceOrderWithTasks,
  CreateServiceOrderRequest,
  UpdateServiceOrderRequest
} from '../types/serviceOrder';
import { 
  Task,
  TaskArea,
  CreateTaskRequest,
  UpdateTaskRequest
} from '../types/task';
import {
  getServiceOrders,
  updateServiceOrder,
  deleteServiceOrder,
  assignTaskToServiceOrder,
  unassignTaskFromServiceOrder,
  createTask,
  updateTask,
  deleteTask,
  bulkDeleteTasks,
  getHoursBankStatus,
  getHoursBank
} from '../services/api';

const ServiceOrderManagement: React.FC = () => {
  const { projectId, serviceOrderId } = useParams<{ 
    projectId: string; 
    serviceOrderId: string; 
  }>();
  const navigate = useNavigate();

  const [serviceOrder, setServiceOrder] = useState<ServiceOrderWithTasks | null>(null);
  const [unassignedTasks, setUnassignedTasks] = useState<Task[]>([]);
  const [unassignedTickets, setUnassignedTickets] = useState<Ticket[]>([]);
  const [osTickets, setOsTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [editingServiceOrder, setEditingServiceOrder] = useState<ServiceOrderWithTasks | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Ref for debouncing socket-triggered reloads
  const reloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load service order and unassigned tasks
  const loadData = useCallback(async (showLoading = true) => {
    if (!projectId || !serviceOrderId) return;

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const response = await getServiceOrders(projectId);
      const { serviceOrders, unassignedTasks: unassigned } = response.data.data;

      const currentServiceOrder = serviceOrders.find((so: ServiceOrderWithTasks) => so._id === serviceOrderId);

      if (!currentServiceOrder) {
        setError('Ordem de serviço não encontrada');
        return;
      }

      setServiceOrder(currentServiceOrder);
      setUnassignedTasks(unassigned);

      // Load tickets for this project and split into OS-assigned and unassigned
      try {
        const allTickets = await getTickets(projectId);
        const assigned: Ticket[] = [];
        const unassigned2: Ticket[] = [];
        allTickets.forEach(t => {
          const soId = typeof t.serviceOrderId === 'object' ? t.serviceOrderId?._id : t.serviceOrderId;
          if (soId === serviceOrderId) {
            assigned.push(t);
          } else if (!t.serviceOrderId) {
            unassigned2.push(t);
          }
        });
        setOsTickets(assigned);
        setUnassignedTickets(unassigned2);
      } catch (ticketErr) {
        console.error('Error loading tickets:', ticketErr);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados');
      console.error('Erro ao carregar dados:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [projectId, serviceOrderId]);

  // Debounced reload for socket events (silent, no loading spinner)
  const debouncedReload = useCallback(() => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
    }
    reloadTimeoutRef.current = setTimeout(() => {
      loadData(false); // Silent reload
    }, 300);
  }, [loadData]);

  // Initialize Socket.io and load data
  useEffect(() => {
    loadData();

    if (projectId) {
      // Connect to socket and join project room
      socketService.connect();
      socketService.joinProject(projectId);

      // Socket event handlers defined inside useEffect to avoid stale closures
      const handleServiceOrderUpdated = (data: any) => {
        if (data.projectId === projectId && data.serviceOrder._id === serviceOrderId) {
          debouncedReload();
        }
      };

      const handleServiceOrderDeleted = (data: any) => {
        if (data.projectId === projectId && data.serviceOrderId === serviceOrderId) {
          navigate(`/projects/${projectId}`);
        }
      };

      // Task assignment handlers - use debounced reload for consistency
      const handleTaskAssigned = (data: any) => {
        if (data.projectId === projectId) {
          debouncedReload();
        }
      };

      const handleTaskUnassigned = (data: any) => {
        if (data.projectId === projectId) {
          debouncedReload();
        }
      };

      const handleHoursUpdated = (data: any) => {
        if (data.projectId === projectId) {
          debouncedReload();
        }
      };

      // Listen for real-time updates
      socketService.on('service-order:updated', handleServiceOrderUpdated);
      socketService.on('service-order:deleted', handleServiceOrderDeleted);
      socketService.on('task:assigned', handleTaskAssigned);
      socketService.on('task:unassigned', handleTaskUnassigned);
      socketService.on('hours:updated', handleHoursUpdated);

      return () => {
        // Clear any pending debounced reload
        if (reloadTimeoutRef.current) {
          clearTimeout(reloadTimeoutRef.current);
        }
        socketService.off('service-order:updated', handleServiceOrderUpdated);
        socketService.off('service-order:deleted', handleServiceOrderDeleted);
        socketService.off('task:assigned', handleTaskAssigned);
        socketService.off('task:unassigned', handleTaskUnassigned);
        socketService.off('hours:updated', handleHoursUpdated);
        socketService.leaveProject(projectId);
      };
    }
  }, [projectId, serviceOrderId, loadData, debouncedReload, navigate]);

  // Check if task assignment would exceed hours limit and return warning
  const checkHoursAvailability = async (taskId: string, targetServiceOrderId: string): Promise<{ warning?: string }> => {
    try {
      // Check if hours tracking is enabled
      const statusResponse = await getHoursBankStatus(projectId!);
      if (!statusResponse.data.data.hoursTrackingEnabled) {
        return {}; // No hours tracking, no warning needed
      }

      // Find the task being moved
      let task: Task | undefined;
      
      // Search in unassigned tasks
      task = unassignedTasks.find(t => t._id === taskId);
      
      // If not found, search in service order tasks
      if (!task && serviceOrder) {
        for (const taskArray of Object.values(serviceOrder.tasksByArea)) {
          task = taskArray.tasks.find(t => t._id === taskId);
          if (task) break;
        }
      }

      if (!task) {
        return {}; // Task not found, no warning
      }

      // Get target service order to determine month/year
      if (!serviceOrder || serviceOrder._id !== targetServiceOrderId) {
        return {}; // Service order not found, no warning
      }

      // Use billing period from service order, not start date
      const month = serviceOrder.billingMonth;
      const year = serviceOrder.billingYear;

      // Get hours bank data
      const hoursResponse = await getHoursBank(projectId!, year, month);
      if (!hoursResponse.data.data.enabled) {
        return {}; // Hours bank not configured for this month, no warning
      }

      const areaBank = hoursResponse.data.data.hoursBank[task.area];
      if (!areaBank) {
        return {}; // Area not configured, no warning
      }

      if (areaBank.availableHours < task.horasEstimadas) {
        const excessHours = task.horasEstimadas - areaBank.availableHours;
        return { 
          warning: `Atenção: Esta atribuição excederá o banco de horas da área ${task.area} em ${excessHours}h. Deseja continuar?` 
        };
      }

      return {}; // No issues, no warning
    } catch (error) {
      console.error('Error checking hours availability:', error);
      return {}; // Allow assignment on error
    }
  };

  // Optimistic update functions
  const optimisticMoveTaskToServiceOrder = (taskId: string, targetArea: TaskArea) => {
    // Find task in unassigned tasks
    const task = unassignedTasks.find(t => t._id === taskId);
    if (!task) return null;

    // Remove from unassigned tasks
    setUnassignedTasks(prev => prev.filter(t => t._id !== taskId));
    
    // Add to service order
    setServiceOrder(prev => {
      if (!prev) return prev;
      
      const updatedTasksByArea = { ...prev.tasksByArea };
      
      // Initialize area if it doesn't exist
      if (!updatedTasksByArea[targetArea]) {
        updatedTasksByArea[targetArea] = {
          tasks: [],
          totalHoras: 0,
          count: 0
        };
      }
      
      // Add task to target area
      const currentAreaData = updatedTasksByArea[targetArea];
      updatedTasksByArea[targetArea] = {
        ...currentAreaData,
        tasks: [...(currentAreaData?.tasks || []), task],
        totalHoras: (currentAreaData?.totalHoras || 0) + task.horasEstimadas,
        count: (currentAreaData?.count || 0) + 1
      };
      
      return {
        ...prev,
        tasksByArea: updatedTasksByArea
      };
    });

    return task;
  };

  const optimisticMoveTaskToUnassigned = (taskId: string) => {
    let foundTask: Task | null = null;
    
    // Find and remove task from service order
    setServiceOrder(prev => {
      if (!prev) return prev;
      
      const updatedTasksByArea = { ...prev.tasksByArea };
      
      // Find and remove task from any area
      for (const area in updatedTasksByArea) {
        const areaData = updatedTasksByArea[area as TaskArea];
        if (areaData) {
          const taskIndex = areaData.tasks.findIndex((t: Task) => t._id === taskId);
          if (taskIndex !== -1) {
            foundTask = areaData.tasks[taskIndex];
            areaData.tasks.splice(taskIndex, 1);
            areaData.totalHoras -= foundTask.horasEstimadas;
            areaData.count -= 1;
            break;
          }
        }
      }
      
      return {
        ...prev,
        tasksByArea: updatedTasksByArea
      };
    });
    
    // Add task to unassigned tasks
    if (foundTask) {
      setUnassignedTasks(prev => [...prev, foundTask as Task]);
    }

    return foundTask;
  };

  const rollbackOptimisticUpdate = (
    taskId: string, 
    operation: 'assign' | 'unassign', 
    originalTask: Task,
    targetArea?: TaskArea
  ) => {
    if (operation === 'assign') {
      // Rollback: remove from service order, add back to unassigned
      optimisticMoveTaskToUnassigned(taskId);
    } else {
      // Rollback: remove from unassigned, add back to service order
      if (targetArea) {
        setUnassignedTasks(prev => prev.filter(t => t._id !== taskId));
        setServiceOrder(prev => {
          if (!prev) return prev;
          
          const updatedTasksByArea = { ...prev.tasksByArea };
          
          if (!updatedTasksByArea[targetArea]) {
            updatedTasksByArea[targetArea] = {
              tasks: [],
              totalHoras: 0,
              count: 0
            };
          }
          
          const currentAreaData = updatedTasksByArea[targetArea]!; // Non-null assertion since we just created it
          updatedTasksByArea[targetArea] = {
            ...currentAreaData,
            tasks: [...currentAreaData.tasks, originalTask],
            totalHoras: currentAreaData.totalHoras + originalTask.horasEstimadas,
            count: currentAreaData.count + 1
          };
          
          return {
            ...prev,
            tasksByArea: updatedTasksByArea
          };
        });
      }
    }
  };

  // Local reordering function for unassigned tasks
  const reorderUnassignedTasks = (taskId: string, sourceIndex: number, destinationIndex: number) => {
    setUnassignedTasks(prev => {
      const tasks = [...prev];
      const [removed] = tasks.splice(sourceIndex, 1);
      tasks.splice(destinationIndex, 0, removed);
      return tasks;
    });
  };

  // Drag and drop handler
  const handleDragEnd = async (result: DropResult) => {
    // Restore user selection
    document.body.style.userSelect = '';

    if (!projectId) return;

    const { destination, source, draggableId } = result;

    if (!destination) return;

    // Same position, no change
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const isTicket = draggableId.startsWith('ticket-');

    // Handle ticket drag-and-drop
    if (isTicket) {
      const ticketId = draggableId.replace('ticket-', '');

      // Reordering within unassigned-tickets (no-op)
      if (source.droppableId === 'unassigned-tickets' && destination.droppableId === 'unassigned-tickets') {
        return;
      }

      try {
        // Assign ticket to OS (from unassigned-tickets to area)
        if (source.droppableId === 'unassigned-tickets' && destination.droppableId !== 'unassigned-tickets') {
          const ticket = unassignedTickets.find(t => t._id === ticketId);
          if (!ticket) return;

          // Optimistic update: move from unassigned to OS
          setUnassignedTickets(prev => prev.filter(t => t._id !== ticketId));
          setOsTickets(prev => [...prev, ticket]);

          try {
            await updateTicketApi(projectId, ticketId, { serviceOrderId: serviceOrderId });
          } catch (err) {
            // Rollback
            setOsTickets(prev => prev.filter(t => t._id !== ticketId));
            setUnassignedTickets(prev => [...prev, ticket]);
            throw err;
          }
        }
        // Unassign ticket from OS (from area to unassigned-tickets)
        else if (destination.droppableId === 'unassigned-tickets') {
          const ticket = osTickets.find(t => t._id === ticketId);
          if (!ticket) return;

          // Optimistic update: move from OS to unassigned
          setOsTickets(prev => prev.filter(t => t._id !== ticketId));
          setUnassignedTickets(prev => [...prev, ticket]);

          try {
            await updateTicketApi(projectId, ticketId, { serviceOrderId: null });
          } catch (err) {
            // Rollback
            setUnassignedTickets(prev => prev.filter(t => t._id !== ticketId));
            setOsTickets(prev => [...prev, ticket]);
            throw err;
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao mover chamado');
      }
      return;
    }

    // Handle task drag-and-drop (existing logic)

    // Handle reordering within unassigned tasks (no API call needed)
    if (
      source.droppableId === 'unassigned-tasks' &&
      destination.droppableId === 'unassigned-tasks'
    ) {
      reorderUnassignedTasks(draggableId, source.index, destination.index);
      return; // Exit early, no API calls needed for local reordering
    }

    // Ignore ticket pool as invalid destination for tasks
    if (destination.droppableId === 'unassigned-tickets') return;

    try {
      const taskId = draggableId;
      let originalTask: Task | null = null;
      let operation: 'assign' | 'unassign' = 'assign';
      let targetArea: TaskArea | undefined;

      // Moving from unassigned to service order
      if (source.droppableId === 'unassigned-tasks') {
        const targetServiceOrderId = destination.droppableId.split('-')[0];
        targetArea = destination.droppableId.split('-')[1] as TaskArea;
        operation = 'assign';

        // Check for hours warning before assignment
        const hoursCheck = await checkHoursAvailability(taskId, targetServiceOrderId);
        if (hoursCheck.warning) {
          const confirmed = window.confirm(hoursCheck.warning);
          if (!confirmed) {
            return; // User cancelled the assignment
          }
        }

        // Optimistic update: move task immediately
        originalTask = optimisticMoveTaskToServiceOrder(taskId, targetArea);
        if (!originalTask) return;

        try {
          await assignTaskToServiceOrder(projectId, targetServiceOrderId, taskId);
        } catch (err) {
          // Rollback on API failure
          rollbackOptimisticUpdate(taskId, operation, originalTask, targetArea);
          throw err;
        }
      }
      // Moving from service order to unassigned
      else if (destination.droppableId === 'unassigned-tasks') {
        const sourceServiceOrderId = source.droppableId.split('-')[0];
        operation = 'unassign';

        // Optimistic update: move task immediately
        originalTask = optimisticMoveTaskToUnassigned(taskId);
        if (!originalTask) return;

        try {
          await unassignTaskFromServiceOrder(projectId, sourceServiceOrderId, taskId);
        } catch (err) {
          // Rollback on API failure - need to find original area
          const originalArea = source.droppableId.split('-')[1] as TaskArea;
          rollbackOptimisticUpdate(taskId, operation, originalTask, originalArea);
          throw err;
        }
      }
      // Moving between service orders (not applicable here, but keeping for consistency)
      else {
        const sourceServiceOrderId = source.droppableId.split('-')[0];
        const destServiceOrderId = destination.droppableId.split('-')[0];

        if (sourceServiceOrderId !== destServiceOrderId) {
          // Check for hours warning before assignment to destination
          const hoursCheck = await checkHoursAvailability(taskId, destServiceOrderId);
          if (hoursCheck.warning) {
            const confirmed = window.confirm(hoursCheck.warning);
            if (!confirmed) {
              return; // User cancelled the move
            }
          }

          // For moves between service orders, we'll do the API calls first
          // since optimistic updates are more complex for this case
          await unassignTaskFromServiceOrder(projectId, sourceServiceOrderId, taskId);
          await assignTaskToServiceOrder(projectId, destServiceOrderId, taskId);
        }
      }

      // Note: Socket.IO events will still fire for multi-user synchronization
      // but our local state is already updated optimistically
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao mover tarefa');
    }
  };

  // Service Order operations
  const handleUpdateServiceOrder = async (data: UpdateServiceOrderRequest) => {
    if (!editingServiceOrder || !projectId) return;
    
    try {
      setFormLoading(true);
      await updateServiceOrder(projectId, editingServiceOrder._id, data);
      await loadData();
      setEditingServiceOrder(null);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao atualizar ordem de serviço');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteServiceOrder = async (serviceOrderId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta ordem de serviço? As tarefas serão movidas para não atribuídas.')) {
      return;
    }

    if (!projectId) return;

    try {
      await deleteServiceOrder(projectId, serviceOrderId);
      navigate(`/projects/${projectId}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir ordem de serviço');
    }
  };

  // Task operations
  const handleCreateTask = async (data: CreateTaskRequest) => {
    if (!projectId) return;

    try {
      setFormLoading(true);
      await createTask(projectId, data);
      await loadData();
      setShowTaskForm(false);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao criar tarefa');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTask = async (data: UpdateTaskRequest) => {
    if (!editingTask || !projectId) return;
    
    try {
      setFormLoading(true);
      await updateTask(projectId, editingTask._id, data);
      await loadData();
      setEditingTask(null);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao atualizar tarefa');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDuplicateTask = async (task: Task) => {
    if (!projectId) return;

    try {
      const duplicatedTask: CreateTaskRequest = {
        name: `${task.name} (Cópia)`,
        description: task.description,
        area: task.area,
        horasEstimadas: task.horasEstimadas,
        prioridade: task.prioridade,
        status: 'pendente'
      };

      setFormLoading(true);
      await createTask(projectId, duplicatedTask);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao duplicar tarefa');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTask = async (task: Task) => {
    if (!projectId) return;

    const confirmed = window.confirm(`Excluir a tarefa "${task.name}"?`);
    if (!confirmed) return;

    try {
      await deleteTask(projectId, task._id);
      // Update local state optimistically
      setUnassignedTasks(prev => prev.filter(t => t._id !== task._id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir tarefa');
      // Reload data to sync state on error
      await loadData();
    }
  };

  const handleBulkDeleteTasks = async (taskIds: string[]) => {
    if (!projectId || taskIds.length === 0) return;

    try {
      await bulkDeleteTasks(projectId, taskIds);
      // Update local state optimistically
      setUnassignedTasks(prev => prev.filter(t => !taskIds.includes(t._id)));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir tarefas');
      // Reload data to sync state on error
      await loadData();
    }
  };

  // Ticket operations
  const handleEditTicket = (ticket: Ticket) => {
    setEditingTicket(ticket);
  };

  const handleDeleteTicket = async (ticket: Ticket) => {
    if (!projectId) return;

    const confirmed = window.confirm(`Excluir o chamado "${ticket.titulo}"?`);
    if (!confirmed) return;

    try {
      await deleteTicketApi(projectId, ticket._id);
      // Remove from whichever list it's in
      setOsTickets(prev => prev.filter(t => t._id !== ticket._id));
      setUnassignedTickets(prev => prev.filter(t => t._id !== ticket._id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir chamado');
    }
  };

  const handleUpdateTicket = async (data: any) => {
    if (!editingTicket || !projectId) return;

    try {
      setFormLoading(true);
      await updateTicketApi(projectId, editingTicket._id, data);
      await loadData(false);
      setEditingTicket(null);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao atualizar chamado');
    } finally {
      setFormLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(`/projects/${projectId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner message="Carregando ordem de serviço" />
        </div>
      </Layout>
    );
  }

  if (error || !serviceOrder) {
    return (
      <Layout>
        <div className="px-6 py-8">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={handleGoBack}
              className="text-foreground-secondary hover:text-foreground-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para o Projeto
            </Button>
          </div>

          <Card variant="glass" className="border-red-500/30 bg-red-500/10">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-heading text-red-400 mb-2">
                {error || 'Ordem de serviço não encontrada'}
              </h3>
              <Button
                variant="gradient"
                onClick={handleGoBack}
              >
                Voltar para o Projeto
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Scrollable page wrapper to enable backlog scrolling */}
      <div className="h-full overflow-y-auto custom-scrollbar">
        <div className="relative w-full h-full">
        <DragDropContext 
          onDragEnd={handleDragEnd}
          onDragStart={() => {
            // Prevent page scrolling during drag
            document.body.style.userSelect = 'none';
          }}
          onDragUpdate={() => {
            // Optional: Add any drag update logic if needed
          }}
        >
          <div className="px-6 py-8 space-y-6">
          {/* Header with breadcrumb */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={handleGoBack}
                className="text-foreground-secondary hover:text-foreground-primary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-heading text-foreground-primary">
                  Ordem de serviço {serviceOrder.name}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/projects/${projectId}/service-orders/${serviceOrderId}/presentation`)}
              >
                <Monitor className="w-4 h-4 mr-2" />
                Visualizar Apresentação
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingServiceOrder(serviceOrder)}
              >
                Editar Ordem de Serviço
              </Button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Card variant="glass" className="border-red-500/30 bg-red-500/10">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{error}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-300"
                >
                  ✕
                </Button>
              </CardContent>
            </Card>
          )}


          {/* Main content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Unassigned Tasks Pool */}
            <div>
              <UnassignedTasksPool
                tasks={unassignedTasks}
                unassignedTickets={unassignedTickets}
                onEditTask={setEditingTask}
                onDuplicateTask={handleDuplicateTask}
                onCreateTask={() => setShowTaskForm(true)}
                onCreateTicket={() => setShowTicketForm(true)}
                onDeleteTask={handleDeleteTask}
                onBulkDeleteTasks={handleBulkDeleteTasks}
                onEditTicket={handleEditTicket}
                onDeleteTicket={handleDeleteTicket}
                projectId={projectId}
                serviceOrderId={serviceOrderId}
              />
            </div>

            {/* Service Order */}
            <div>
              <ServiceOrderManagementCard
                serviceOrder={serviceOrder}
                projectId={projectId!}
                tickets={osTickets}
                onEditTask={setEditingTask}
                onDuplicateTask={handleDuplicateTask}
                onEditTicket={handleEditTicket}
                onDeleteTicket={handleDeleteTicket}
              />
            </div>
          </div>


          {/* Forms */}
          {editingServiceOrder && (
            <ServiceOrderForm
              serviceOrder={editingServiceOrder}
              onSubmit={handleUpdateServiceOrder}
              onCancel={() => setEditingServiceOrder(null)}
              loading={formLoading}
            />
          )}

          {(showTaskForm || editingTask) && (
            <TaskForm
              task={editingTask}
              onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
              onCancel={() => {
                setShowTaskForm(false);
                setEditingTask(null);
              }}
              loading={formLoading}
            />
          )}

          {(showTicketForm || editingTicket) && projectId && (
            <TicketForm
              projectId={projectId}
              ticket={editingTicket || undefined}
              serviceOrderId={serviceOrderId}
              onSubmit={editingTicket ? handleUpdateTicket : async (data) => {
                await createTicket(projectId, data as CreateTicketRequest);
                await loadData(false);
              }}
              onClose={() => {
                setShowTicketForm(false);
                setEditingTicket(null);
              }}
            />
          )}
          </div>
        </DragDropContext>
        </div>
      </div>
    </Layout>
  );
};

export default ServiceOrderManagement;
